import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { deleteRow, insertRow, listRows, updateRow } from "@/lib/adminApi";
import { primeSeoOverride, type PublicSeo } from "@/lib/publicContent";
import { getAbsoluteUrl } from "@/components/feature/Seo";
import { SEO_STATUS, metaFor, optionsFrom, type SeoSetting } from "@/lib/adminContent";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { useAdminToast } from "@/hooks/useAdminToast";
import ItemSeoOverrides from "@/pages/admin/seo/components/ItemSeoOverrides";
import { countSectionOverrides, sectionRootFromWildcard } from "@/pages/admin/seo/sectionItems";

const EMPTY_SEO = {
  page_label: "",
  path: "/",
  title: "",
  description: "",
  keywords: "",
  canonical: "",
  og_image: "",
  status: "published",
};

const previewHref = (path: string): string =>
  getAbsoluteUrl(path.endsWith("/*") ? path.slice(0, -2) : path);

function LengthHint({ value, ideal }: { value: string; ideal: string }) {
  const len = value.length;
  const ok = ideal === "title" ? len >= 30 && len <= 60 : len >= 120 && len <= 160;
  return (
    <span className={`text-[11px] font-semibold ${ok ? "text-primary-600" : "text-accent-700"}`}>
      {len} chars {ok ? "· good length" : ideal === "title" ? "· aim for 30–60" : "· aim for 120–160"}
    </span>
  );
}

function TableGroup({
  icon,
  title,
  hint,
  count,
  action,
  children,
}: {
  icon: string;
  title: string;
  hint: string;
  count: number;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-3">
        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
          <i className={`${icon} text-lg`}></i>
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
            {title}
            <span className="text-[11px] font-semibold text-foreground-600 bg-background-100 border border-background-300 px-2 py-0.5 rounded-full">
              {count}
            </span>
          </h2>
          <p className="text-xs text-foreground-500 mt-0.5">{hint}</p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

function GroupEmpty({ text }: { text: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg py-8 text-center">
      <p className="text-sm text-foreground-500">{text}</p>
    </div>
  );
}

export default function AdminSeo() {
  const [rows, setRows] = useState<SeoSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [overrideRoot, setOverrideRoot] = useState<string | null>(null);
  const [resetRoot, setResetRoot] = useState<string | null>(null);
  const [overridesOnly, setOverridesOnly] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_SEO });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<SeoSetting>("seo_settings", "id"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load SEO settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Push a saved row to the public site so search metadata updates live. */
  const syncPublicSeo = (row: SeoSetting) => {
    const seo: PublicSeo | null =
      row.status === "published"
        ? {
            title: row.title || null,
            description: row.description || null,
            keywords: row.keywords || null,
            image: row.og_image || null,
          }
        : null;
    primeSeoOverride(row.path, seo);
  };

  const update = async (id: number, patch: Partial<SeoSetting>, message?: string) => {
    const current = rows.find((row) => row.id === id);
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("seo_settings", id, patch);
      if (current) syncPublicSeo({ ...current, ...patch });
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createSeo = async () => {
    if (!draft.page_label.trim() || !draft.path.trim()) {
      setError("A page label and path are both required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<SeoSetting>("seo_settings", { ...draft, page_label: draft.page_label.trim() });
      setRows((list) => [...list, created]);
      syncPublicSeo(created);
      setCreateOpen(false);
      setDraft({ ...EMPTY_SEO });
      notify("SEO entry added.");
    } catch {
      setError("We couldn't create that entry. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /** Create or update an exact-path override that sits above the section entry. */
  const saveItemOverride = async (
    path: string,
    label: string,
    patch: { title: string; description: string; keywords: string; og_image: string },
  ) => {
    const existing = rows.find((row) => row.path === path);
    try {
      if (existing) {
        await updateRow("seo_settings", existing.id, patch);
        const updated = { ...existing, ...patch };
        setRows((list) => list.map((row) => (row.id === existing.id ? updated : row)));
        syncPublicSeo(updated);
      } else {
        const created = await insertRow<SeoSetting>("seo_settings", {
          page_label: label,
          path,
          title: patch.title,
          description: patch.description,
          keywords: patch.keywords,
          canonical: "",
          og_image: patch.og_image,
          status: "published",
        });
        setRows((list) => [...list, created]);
        syncPublicSeo(created);
      }
      notify("Per-item SEO override saved.");
    } catch {
      setError("We couldn't save that override. Please try again.");
    }
  };

  /** Remove a per-item override so the page falls back to the section default. */
  const resetItemOverride = async (path: string) => {
    const existing = rows.find((row) => row.path === path);
    if (!existing) return;
    try {
      await deleteRow("seo_settings", existing.id);
      primeSeoOverride(path, null);
      setRows((list) => list.filter((row) => row.id !== existing.id));
      notify("Override removed — using the section default.");
    } catch {
      setError("We couldn't remove that override. Please try again.");
    }
  };

  /** Clear every per-item override under a section in one go. */
  const resetSectionOverrides = async (sectionRoot: string) => {
    const targets = rows.filter(
      (row) => !row.path.endsWith("/*") && row.path.startsWith(`${sectionRoot}/`),
    );
    if (targets.length === 0) return;
    try {
      for (const row of targets) {
        await deleteRow("seo_settings", row.id);
        primeSeoOverride(row.path, null);
      }
      const ids = new Set(targets.map((row) => row.id));
      setRows((list) => list.filter((row) => !ids.has(row.id)));
      notify(`Cleared ${targets.length} item override${targets.length === 1 ? "" : "s"}.`);
    } catch {
      setError("We couldn't clear those overrides. Please try again.");
    }
  };

  const remove = async (id: number) => {
    const current = rows.find((row) => row.id === id);
    try {
      await deleteRow("seo_settings", id);
      if (current) primeSeoOverride(current.path, null);
      setRows((list) => list.filter((row) => row.id !== id));
      setSelectedId(null);
      notify("SEO entry deleted.");
    } catch {
      setError("We couldn't delete that entry. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.page_label, row.path, row.title || "", row.keywords || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter]);

  const pageRows = useMemo(() => filtered.filter((row) => !row.path.endsWith("/*")), [filtered]);
  const sectionRows = useMemo(() => filtered.filter((row) => row.path.endsWith("/*")), [filtered]);

  const visibleSections = useMemo(() => {
    if (!overridesOnly) return sectionRows;
    return sectionRows.filter(
      (row) => countSectionOverrides(rows, sectionRootFromWildcard(row.path)) > 0,
    );
  }, [sectionRows, overridesOnly, rows]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const overrideSectionLabel = overrideRoot
    ? rows.find((row) => row.path === `${overrideRoot}/*`)?.page_label || "Section"
    : undefined;
  const optimised = rows.filter((row) => (row.title || "").length >= 30 && (row.description || "").length >= 120).length;

  const baseColumns: Column<SeoSetting>[] = [
    {
      key: "page_label",
      header: "Page",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.page_label}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.path}</p>
        </div>
      ),
    },
    { key: "title", header: "SEO title", render: (row) => <span className="text-sm text-foreground-700 line-clamp-1 max-w-xs">{row.title || "—"}</span> },
    { key: "keywords", header: "Keywords", render: (row) => <span className="text-xs text-foreground-600 line-clamp-1 max-w-xs">{row.keywords || "—"}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(SEO_STATUS, row.status)} /> },
  ];

  const sectionColumns: Column<SeoSetting>[] = [
    ...baseColumns.slice(0, 3),
    {
      key: "overrides",
      header: "Item overrides",
      render: (row) => {
        const count = countSectionOverrides(rows, sectionRootFromWildcard(row.path));
        return count > 0 ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-accent-100 text-accent-900 border border-accent-300 px-2 py-0.5 rounded-full whitespace-nowrap">
            <i className="ri-pencil-line"></i> {count} custom
          </span>
        ) : (
          <span className="text-xs text-foreground-500 whitespace-nowrap">Inherited</span>
        );
      },
    },
    ...baseColumns.slice(3),
  ];

  const previewAction = (row: SeoSetting) => (
    <a
      href={previewHref(row.path)}
      target="_blank"
      rel="noopener noreferrer"
      title="Preview live page"
      className="inline-flex items-center gap-1.5 border border-background-300 text-foreground-700 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
    >
      <i className="ri-external-link-line"></i> Preview
    </a>
  );

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="SEO"
        subtitle="Search titles, meta descriptions and keywords for every public page — the settings that decide how you show up in search."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Page Entry
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Pages tracked", value: rows.length, icon: "ri-seo-line", tone: "primary" },
          { label: "Active", value: rows.filter((row) => row.status === "published").length, icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "Well-optimised", value: optimised, icon: "ri-thumb-up-line", tone: "secondary" },
          { label: "Needs attention", value: rows.length - optimised, icon: "ri-error-warning-line", tone: "accent" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by page, path, keyword…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(SEO_STATUS)]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} entries</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading SEO settings…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-seo-line" title={rows.length === 0 ? "No SEO entries yet" : "No pages match"} message="Add a page to manage its search metadata." />
        ) : (
          <div className="space-y-7">
            <TableGroup
              icon="ri-file-text-line"
              title="Pages"
              count={pageRows.length}
              hint="Static pages, each with its own search title and description."
            >
              {pageRows.length > 0 ? (
                <DataTable
                  columns={baseColumns}
                  rows={pageRows}
                  onRowClick={(row) => setSelectedId(row.id)}
                  rowActions={(row) => previewAction(row)}
                />
              ) : (
                <GroupEmpty text="No pages match your filters." />
              )}
            </TableGroup>

            <TableGroup
              icon="ri-layout-masonry-line"
              title="Sections"
              count={sectionRows.length}
              hint="Section-wide defaults for dynamic detail pages. The badge shows how many items have their own title."
              action={
                <button
                  type="button"
                  onClick={() => setOverridesOnly((value) => !value)}
                  title="Show only sections where items already have custom titles"
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                    overridesOnly
                      ? "bg-accent-100 text-accent-900 border-accent-300"
                      : "bg-background-100 text-foreground-700 border-background-300 hover:bg-background-200"
                  }`}
                >
                  <i className="ri-filter-3-line"></i>
                  {overridesOnly ? "Only with overrides" : "Sections with overrides"}
                </button>
              }
            >
              {visibleSections.length > 0 ? (
                <DataTable
                  columns={sectionColumns}
                  rows={visibleSections}
                  onRowClick={(row) => setSelectedId(row.id)}
                  rowActions={(row) => {
                    const root = sectionRootFromWildcard(row.path);
                    const count = countSectionOverrides(rows, root);
                    return (
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setOverrideRoot(root)}
                          title="Add a per-item override"
                          className="inline-flex items-center gap-1.5 border border-background-300 text-foreground-700 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-add-line"></i> Item override
                        </button>
                        {count > 0 ? (
                          <button
                            type="button"
                            onClick={() => setResetRoot(root)}
                            title="Reset every item override in this section"
                            className="inline-flex items-center gap-1.5 border border-accent-300 text-accent-900 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-accent-100 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-eraser-line"></i> Reset all
                          </button>
                        ) : null}
                        {previewAction(row)}
                      </div>
                    );
                  }}
                />
              ) : (
                <GroupEmpty
                  text={
                    overridesOnly && sectionRows.length > 0
                      ? "No section has item overrides yet."
                      : "No sections match your filters."
                  }
                />
              )}
            </TableGroup>
          </div>
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.page_label || "SEO"}
        subtitle={selected?.path}
        footer={
          selected ? (
            <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
              <i className="ri-delete-bin-line"></i> Delete entry
            </button>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(SEO_STATUS, selected.status)} />
              <span className="text-xs font-semibold text-foreground-800 bg-background-100 border border-background-300 px-2.5 py-1 rounded-full">{selected.path}</span>
            </div>

            <a
              href={previewHref(selected.path)}
              target="_blank"
              rel="noopener noreferrer"
              className={`${btnGhost} w-full justify-center`}
            >
              <i className="ri-external-link-line"></i> Preview live page
            </a>

            {selected.path.endsWith("/*") ? (
              <>
                <div className="flex items-start gap-2 text-xs text-foreground-600 bg-secondary-100/70 border border-secondary-200 rounded-md px-3 py-2.5">
                  <span className="w-5 h-5 flex items-center justify-center text-secondary-700 shrink-0">
                    <i className="ri-lightbulb-line"></i>
                  </span>
                  <span>
                    Section-wide default — it applies to every detail page that doesn't have its own
                    override below. Keep <strong>{"{title}"}</strong> and{" "}
                    <strong>{"{description}"}</strong> in the text to insert each item's own name and
                    summary.
                  </span>
                </div>

                <ItemSeoOverrides
                  sectionRoot={selected.path.replace(/\/\*$/, "")}
                  rows={rows}
                  onSave={saveItemOverride}
                  onReset={resetItemOverride}
                />
              </>
            ) : null}

            <Field label="SEO title">
              <input value={selected.title || ""} onChange={(e) => update(selected.id, { title: e.target.value })} className={textInput} />
              <div className="mt-1.5"><LengthHint value={selected.title || ""} ideal="title" /></div>
            </Field>

            <Field label="Meta description">
              <textarea value={selected.description || ""} onChange={(e) => update(selected.id, { description: e.target.value })} rows={3} maxLength={320} className={`${textInput} resize-none`}></textarea>
              <div className="mt-1.5"><LengthHint value={selected.description || ""} ideal="description" /></div>
            </Field>

            <Field label="Keywords">
              <input value={selected.keywords || ""} onChange={(e) => update(selected.id, { keywords: e.target.value })} placeholder="comma, separated, keywords" className={textInput} />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Canonical URL">
                <input value={selected.canonical || ""} onChange={(e) => update(selected.id, { canonical: e.target.value })} className={textInput} />
              </Field>
              <Field label="Status">
                <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value }, "SEO status updated.")} className={fieldInput}>
                  {optionsFrom(SEO_STATUS).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                </select>
              </Field>
            </div>

            <Field label="Search preview">
              <div className="border border-background-200 rounded-md p-4 bg-background-50">
                <p className="text-xs text-primary-700 truncate">{selected.title || "Page title"}</p>
                <p className="text-sm text-foreground-800 mt-1 line-clamp-2">{selected.description || "Meta description preview…"}</p>
              </div>
            </Field>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add a page"
        subtitle="New SEO entry"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>Cancel</button>
            <button type="button" onClick={createSeo} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Add entry
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Page label">
            <input value={draft.page_label} onChange={(e) => setDraft({ ...draft, page_label: e.target.value })} placeholder="e.g. Gallery" className={textInput} />
          </TextField>
          <TextField label="Path">
            <input value={draft.path} onChange={(e) => setDraft({ ...draft, path: e.target.value })} placeholder="/gallery" className={textInput} />
          </TextField>
        </div>
        <TextField label="SEO title">
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={textInput} />
        </TextField>
        <TextField label="Meta description">
          <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={3} maxLength={320} className={`${textInput} resize-none`}></textarea>
        </TextField>
        <TextField label="Keywords">
          <input value={draft.keywords} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} placeholder="comma, separated, keywords" className={textInput} />
        </TextField>
      </Modal>

      <Modal
        open={Boolean(overrideRoot)}
        onClose={() => setOverrideRoot(null)}
        title="Per-item SEO overrides"
        subtitle={overrideSectionLabel}
        size="lg"
        footer={
          <button type="button" onClick={() => setOverrideRoot(null)} className={btnGhost}>
            Done
          </button>
        }
      >
        {overrideRoot ? (
          <ItemSeoOverrides
            sectionRoot={overrideRoot}
            rows={rows}
            onSave={saveItemOverride}
            onReset={resetItemOverride}
            embedded
          />
        ) : null}
      </Modal>

      <Modal
        open={Boolean(resetRoot)}
        onClose={() => setResetRoot(null)}
        title="Reset all overrides"
        subtitle={resetRoot ? rows.find((row) => row.path === `${resetRoot}/*`)?.page_label : undefined}
        footer={
          <>
            <button type="button" onClick={() => setResetRoot(null)} className={btnGhost}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                const root = resetRoot;
                setResetRoot(null);
                if (root) resetSectionOverrides(root);
              }}
              className={btnPrimary}
            >
              <i className="ri-eraser-line"></i> Reset all overrides
            </button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          This clears every per-item override under this section
          {resetRoot
            ? ` (${countSectionOverrides(rows, resetRoot)} ${
                countSectionOverrides(rows, resetRoot) === 1 ? "page" : "pages"
              })`
            : ""}
          . Those pages will fall back to the section default. This can't be undone.
        </p>
      </Modal>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically and go live on the site right away.
      </p>
      {node}
    </div>
  );
}